import { Slot } from "@radix-ui/react-slot";
import { IconLoader2 } from "@tabler/icons-react";
import { type VariantProps, cva } from "class-variance-authority";
import clsx from "clsx";
import * as React from "react";

const buttonVariants = cva(
	"inline-flex items-center justify-center whitespace-nowrap font-semibold transition-colors outline-none focus-visible:outline-none focus-visible:ring-2 ring-offset-0 focus-visible:ring-focus-ring/30 disabled:!bg-slate-100 disabled:!border-slate-100 disabled:!text-foreground/50 disabled:pointer-events-none",
	{
		variants: {
			variant: {
				default:
					"bg-primary-600 border-2 border-primary-600 hover:bg-primary-500 hover:border-primary-500 text-primary-foreground",
				secondary:
					"bg-secondary-600 border-2 border-secondary-600 hover:bg-secondary-500 hover:border-secondary-500 text-secondary-foreground",
				danger:
					"bg-danger-600 border-2 border-danger-600 hover:bg-danger-500 hover:border-danger-500 text-danger-foreground",
				outline:
					"bg-transparent border border-primary-600 hover:bg-primary-600 text-primary-600 hover:text-primary-foreground",
				ghost:
					"bg-slate-200 border-2 border-slate-200 hover:bg-slate-100 hover:border-slate-100 text-foreground shadow-none",
				link: "text-blue-600 underline-offset-2 hover:underline shadow-none px-0 h-auto",
				green:
					"bg-green-600 border-2 border-green-600 hover:bg-green-500 hover:border-green-500 text-white",
			},
			size: {
				default: "h-[38px] px-3 gap-1 text-base tracking-tight rounded-md",
				xs: "h-[20px] px-1.5 text-xs gap-1 tracking-tight rounded",
				sm: "h-[28px] px-2 text-sm gap-1 tracking-tight rounded-md",
				lg: "h-[48px] px-6 text-lg gap-2.5 rounded-lg",
				icon: "h-[28px] w-[28px] text-sm rounded-md",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size = "default",
			asChild = false,
			loading = false,
			disabled = false,
			children,
			...props
		},
		ref,
	) => {
		const Comp = asChild ? Slot : "button";

		const iconSizes: Record<string, number> = {
			xs: 10,
			sm: 14,
			default: 16,
			lg: 18,
			icon: 16,
		};

		return (
			<Comp
				className={clsx(
					buttonVariants({ variant, size, className }),
					(loading || disabled) &&
						"bg-slate-100 border-2 border-slate-100 !text-foreground/50 pointer-events-none",
				)}
				ref={ref}
				disabled={disabled || loading}
				{...props}
			>
				{loading && (
					<div className="inline-flex">
						<IconLoader2
							className="animate-spin"
							size={iconSizes[size]}
							stroke={2}
						/>
					</div>
				)}
				{loading ? size !== "icon" && "Loading" : children}
			</Comp>
		);
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
